<template>
  <div>
    <!-- Basic Invoice Information -->
    <h3 class="mb-4 mt-4">{{ $t('form.basicInvoiceInformation') }}</h3>
    <div class="row g-3">
      <div class="col-md-6">
        <div class="form-group">
          <label for="invoiceNumber" class="form-label">
            <span data-bs-toggle="tooltip"
                  :data-bs-title="$t('tooltips.invoiceNumber')"
                  class="badge ehw-green">BT-1</span>{{ $t('form.invoiceNumber') }}
          </label>
          <input
              type="text"
              class="form-control"
              :class="{ 'is-invalid': uxStore.hasParsingError('InvoiceNumber') }"
              id="invoiceNumber"
              v-model="localInvoice.invoiceNumber"
              @blur="emitUpdate('InvoiceNumber')"
              required
          />
          <div v-if="uxStore.hasParsingError('InvoiceNumber')" class="invalid-feedback">
            {{ uxStore.getParsingErrorMessage('InvoiceNumber') }}
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="form-group">
          <label for="invoiceDate" class="form-label">
            <span data-bs-toggle="tooltip"
                  :data-bs-title="$t('tooltips.invoiceDate')"
                  class="badge ehw-green">BT-2</span>{{ $t('form.invoiceDate') }}
          </label>
          <input
              type="date"
              class="form-control"
              :class="{ 'is-invalid': uxStore.hasParsingError('InvoiceDate') }"
              id="invoiceDate"
              v-model="formattedInvoiceDate"
              @change="updateInvoiceDate"
              required
          />
          <div v-if="uxStore.hasParsingError('InvoiceDate')" class="invalid-feedback">
            {{ uxStore.getParsingErrorMessage('InvoiceDate') }}
          </div>
        </div>
      </div>
    </div>

    <div class="row g-3 mt-2">
      <div class="col-md-6">
        <div class="form-group">
          <label for="billingPeriodStart" class="form-label"><span data-bs-toggle="tooltip"
                                                                   :data-bs-title="$t('tooltips.invoiceStartDate')"
                                                                   class="badge ehw-green">BT-73</span>{{
              $t('form.billingPeriodStart')
            }}
          </label>
          <input
              type="date"
              class="form-control"
              :class="{ 'is-invalid': uxStore.hasParsingError('BillingPeriodStart') }"
              id="billingPeriodStart"
              v-model="formattedBillingPeriodStart"
              @change="updateBillingPeriodStart"
          />
          <div v-if="uxStore.hasParsingError('BillingPeriodStart')" class="invalid-feedback">
            {{ uxStore.getParsingErrorMessage('BillingPeriodStart') }}
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="form-group">
          <label for="billingPeriodEnd" class="form-label"><span data-bs-toggle="tooltip"
                                                                 :data-bs-title="$t('tooltips.invoiceEndDate')"
                                                                 class="badge ehw-green">BT-74</span>{{
              $t('form.billingPeriodEnd')
            }}
          </label>
          <input
              type="date"
              class="form-control"
              :class="{ 'is-invalid': uxStore.hasParsingError('BillingPeriodEnd') }"
              id="billingPeriodEnd"
              v-model="formattedBillingPeriodEnd"
              @change="updateBillingPeriodEnd"
          />
          <div v-if="uxStore.hasParsingError('BillingPeriodEnd')" class="invalid-feedback">
            {{ uxStore.getParsingErrorMessage('BillingPeriodEnd') }}
          </div>
        </div>
      </div>
    </div>

    <div class="row g-3 mt-2">
      <div class="col-md-6">
        <div class="form-group">
          <SearchableDropdown
              input-id="currencyCode"
              :label="$t('form.currencyCode')"
              :model-value="localInvoice.currencyCode"
              @update:modelValue="val => { localInvoice.currencyCode = val; emitUpdate('CurrencyCode'); }"
              :options="masterDataStore.currencyCodes"
              :has-parsing-error="uxStore.hasParsingError('CurrencyCode')"
              bt-number="BT-5"
              :bt-tooltip="$t('tooltips.currencyCode')"
          />
          <div v-if="uxStore.hasParsingError('CurrencyCode')" class="invalid-feedback" style="padding-right: 27px;">
            <p>
              {{ uxStore.getParsingErrorMessage('CurrencyCode') }}
            </p>
            <button
                data-bs-toggle="tooltip"
                :data-bs-title="$t('tooltips.useDefaultValues')"
                class="btn py-0" type="button" @click.prevent="clearParsingErrorWithTooltip('CurrencyCode', $event)">
              <svg data-slot="icon" style="width: 27px; height: 27px; color: #dc3545" fill="none" stroke-width="1.5"
                   stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round"
                      d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="form-group">
          <label for="standardTaxRate" class="form-label"><span data-bs-toggle="tooltip"
                                                                :data-bs-title="$t('tooltips.standardTaxRate')"
                                                                class="badge ehw-green">BT-152</span>{{
              $t('form.standardTaxRate')
            }}</label>
          <input
              type="number"
              step="0.01"
              class="form-control"
              :class="{ 'is-invalid': uxStore.hasParsingError('StandardTaxRate') }"
              id="standardTaxRate"
              v-model.number="localInvoice.standardTaxRate"
              @blur="emitUpdate('StandardTaxRate')"
          />
          <div v-if="uxStore.hasParsingError('StandardTaxRate')" class="invalid-feedback">
            {{ uxStore.getParsingErrorMessage('StandardTaxRate') }}
          </div>
        </div>
      </div>
    </div>

    <div class="row g-3 mt-2">
      <div class="col-md-3">
        <div class="form-group">
          <label for="reducedTaxRate" class="form-label"><span data-bs-toggle="tooltip"
                                                               :data-bs-title="$t('tooltips.reducedTaxRate')"
                                                               class="badge ehw-green">BT-119</span>{{
              $t('form.reducedTaxRate')
            }}</label>
          <input
              type="number"
              step="0.01"
              class="form-control"
              :class="{ 'is-invalid': uxStore.hasParsingError('ReducedTaxRate') }"
              id="reducedTaxRate"
              v-model.number="localInvoice.reducedTaxRate"
              @blur="emitUpdate('ReducedTaxRate')"
          />
          <div v-if="uxStore.hasParsingError('ReducedTaxRate')" class="invalid-feedback">
            {{ uxStore.getParsingErrorMessage('ReducedTaxRate') }}
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="form-group">
          <label for="taxExemptionReasonCode" class="form-label">{{ $t('form.taxExemptionReasonCode') }}</label>
          <input
              type="text"
              class="form-control"
              :class="{ 'is-invalid': uxStore.hasParsingError('TaxExemptionReasonCode') }"
              id="taxExemptionReasonCode"
              v-model.number="localInvoice.taxExemptionReasonCode"
              disabled
          />
        </div>
      </div>
      <div class="col-md-6">
        <div class="form-group">
          <label for="businessProcess" class="form-label">{{ $t('form.businessProcess') }}</label>
          <textarea
              type="text"
              class="form-control"
              :class="{ 'is-invalid': uxStore.hasParsingError('BusinessProcess') }"
              id="businessProcess"
              v-model="localInvoice.businessProcess"
              @blur="emitUpdate('BusinessProcess')"
          />
          <div v-if="uxStore.hasParsingError('BusinessProcess')" class="invalid-feedback">
            {{ uxStore.getParsingErrorMessage('BusinessProcess') }}
          </div>
        </div>
      </div>
    </div>

    <!-- Seller Information -->
    <h3 class="mt-5 mb-4">{{ $t('form.sellerInformation') }}</h3>
    <div class="row g-3">
      <div class="col-md-6">
        <div class="form-group">
          <label for="sellerName" class="form-label">
            <span data-bs-toggle="tooltip"
                  :data-bs-title="$t('tooltips.sellerName')"
                  class="badge ehw-green">
              BT-27
            </span>
            <span data-bs-toggle="tooltip"
                  :data-bs-title="$t('tooltips.sellerPaymentAccountName')"
                  class="badge ehw-green">
              BT-85
            </span>
            {{ $t('form.sellerName') }}
          </label>
          <input
              type="text"
              class="form-control"
              :class="{ 'is-invalid': uxStore.hasParsingError('SellerName') }"
              id="sellerName"
              v-model="localInvoice.sellerName"
              @blur="emitUpdate('SellerName')"
          />
          <div v-if="uxStore.hasParsingError('SellerName')" class="invalid-feedback">
            {{ uxStore.getParsingErrorMessage('SellerName') }}
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="form-group">
          <label for="sellerPostcode" class="form-label">
            <span data-bs-toggle="tooltip"
                  :data-bs-title="$t('tooltips.sellerPostcode')"
                  class="badge ehw-green">BT-38</span>{{ $t('form.sellerPostcode') }}
          </label>
          <input
              type="text"
              class="form-control"
              :class="{ 'is-invalid': uxStore.hasParsingError('SellerPostcode') }"
              id="sellerPostcode"
              v-model="localInvoice.sellerPostcode"
              @blur="emitUpdate('SellerPostcode')"
          />
          <div v-if="uxStore.hasParsingError('SellerPostcode')" class="invalid-feedback">
            {{ uxStore.getParsingErrorMessage('SellerPostcode') }}
          </div>
        </div>
      </div>
    </div>

    <div class="row g-3 mt-2">
      <div class="col-md-6">
        <div class="form-group">
          <label for="sellerCity" class="form-label">
            <span data-bs-toggle="tooltip"
                  :data-bs-title="$t('tooltips.sellerCity')"
                  class="badge ehw-green">BT-37</span>{{ $t('form.sellerCity') }}
          </label>
          <input
              type="text"
              class="form-control"
              :class="{ 'is-invalid': uxStore.hasParsingError('SellerCity') }"
              id="sellerCity"
              v-model="localInvoice.sellerCity"
              @blur="emitUpdate('SellerCity')"
          />
          <div v-if="uxStore.hasParsingError('SellerCity')" class="invalid-feedback">
            {{ uxStore.getParsingErrorMessage('SellerCity') }}
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="form-group">
          <label for="sellerStreet" class="form-label">
            <span data-bs-toggle="tooltip"
                  :data-bs-title="$t('tooltips.sellerStreet')"
                  class="badge ehw-green">BT-35</span>{{ $t('form.sellerStreet') }}
          </label>
          <input
              type="text"
              class="form-control"
              :class="{ 'is-invalid': uxStore.hasParsingError('SellerStreet') }"
              id="sellerStreet"
              v-model="localInvoice.sellerStreet"
              @blur="emitUpdate('SellerStreet')"
          />
          <div v-if="uxStore.hasParsingError('SellerStreet')" class="invalid-feedback">
            {{ uxStore.getParsingErrorMessage('SellerStreet') }}
          </div>
        </div>
      </div>
    </div>

    <div class="row g-3 mt-2">
      <div class="col-md-6">
        <div class="form-group">
          <SearchableDropdown
              input-id="sellerCountryCode"
              :label="$t('form.sellerCountryCode')"
              :model-value="localInvoice.sellerCountryCode"
              @update:modelValue="val => { localInvoice.sellerCountryCode = val; emitUpdate('SellerCountryCode'); }"
              :options="masterDataStore.countryCodes"
              :has-parsing-error="uxStore.hasParsingError('SellerCountryCode')"
              bt-number="BT-40"
              :bt-tooltip="$t('tooltips.sellerCountryCode')"
          />
          <div v-if="uxStore.hasParsingError('SellerCountryCode')" class="invalid-feedback"
               style="padding-right: 27px;">
            <p>
              {{ uxStore.getParsingErrorMessage('SellerCountryCode') }}
            </p>
            <button
                data-bs-toggle="tooltip"
                :data-bs-title="$t('tooltips.useDefaultValues')"
                class="btn py-0" type="button"
                @click.prevent="clearParsingErrorWithTooltip('SellerCountryCode', $event)">
              <svg data-slot="icon" style="width: 27px; height: 27px; color: #dc3545" fill="none" stroke-width="1.5"
                   stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round"
                      d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="form-group">
          <label for="sellerGlobalID" class="form-label"><span data-bs-toggle="tooltip"
                                                               :data-bs-title="$t('tooltips.sellerGlobalID')"
                                                               class="badge ehw-green">BT-29</span>{{
              $t('form.sellerGlobalID')
            }}</label>
          <input
              type="text"
              class="form-control"
              :class="{ 'is-invalid': uxStore.hasParsingError('SellerGlobalID') }"
              id="sellerGlobalID"
              v-model="localInvoice.sellerGlobalID"
              @blur="emitUpdate('SellerGlobalID')"
          />
          <div v-if="uxStore.hasParsingError('SellerGlobalID')" class="invalid-feedback">
            {{ uxStore.getParsingErrorMessage('SellerGlobalID') }}
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="form-group">
          <SearchableDropdown input-id="sellerGlobalIDSchemeIdentifier"
                              :label="$t('form.sellerGlobalIDSchemeIdentifier')"
                              :model-value="localInvoice.sellerGlobalIDSchemeIdentifier"
                              :has-parsing-error="uxStore.hasParsingError('SellerGlobalIDSchemeIdentifier')"
                              :options="masterDataStore.globalIdSchemeIdentifiers"
                              @update:modelValue="val => { localInvoice.sellerGlobalIDSchemeIdentifier = val; emitUpdate('SellerGlobalIDSchemeIdentifier'); }"

          />
          <div v-if="uxStore.hasParsingError('SellerGlobalIDSchemeIdentifier')" class="invalid-feedback">
            {{ uxStore.getParsingErrorMessage('SellerGlobalIDSchemeIdentifier') }}
          </div>
        </div>
      </div>
    </div>
    <div class="row g-3 mt-2">
      <div class="col-md-6">
        <div class="form-group">
          <label for="sellerElectronicAddress" class="form-label"><span data-bs-toggle="tooltip"
                                                                        :data-bs-title="$t('tooltips.sellerElectronicAddress')"
                                                                        class="badge ehw-green">BT-34</span>{{
              $t('form.sellerElectronicAddress')
            }}</label>
          <input
              type="text"
              class="form-control"
              :class="{ 'is-invalid': uxStore.hasParsingError('SellerElectronicAddress') }"
              id="sellerElectronicAddress"
              v-model="localInvoice.sellerElectronicAddress"
              @blur="emitUpdate('SellerElectronicAddress')"
          />
          <div v-if="uxStore.hasParsingError('SellerElectronicAddress')" class="invalid-feedback">
            {{ uxStore.getParsingErrorMessage('SellerElectronicAddress') }}
          </div>
        </div>
      </div>
    </div>

    <!-- Seller Contact Toggle and Form -->
    <h3 class="mt-5 mb-4 d-flex justify-content-start gap-3 align-items-center">
      <span>{{ $t('form.sellerContact') }}</span>
      <!-- Bootstrap Switch for Seller Contact -->
      <span class="form-check form-switch mb-0 d-flex justify-content-start mb-2">
        <input
            class="form-check-input"
            :class="{ 'is-invalid': uxStore.hasParsingError('SellerContact') }"
            type="checkbox"
            id="toggleSellerContact"
            v-model="showSellerContact"
            style="background-color: #ADC22D; border-color: #ADC22D;"
        />
      </span>
    </h3>
    <ContactForm
        v-if="showSellerContact"
        :contact="localInvoice.sellerContact"
        label="Seller Contact"
        @update-contact="updateSellerContact"
    />

    <h3 class="mt-5 mb-4">{{ $t('form.sellerTaxRegistration') }}</h3>
    <TaxRegistrationForm
        :taxRegistration="localInvoice.sellerTaxRegistrations"
        @update-tax-registration="updateSellerTaxRegistration"
        bt-number="BT-31/32"
        :bt-tooltip="$t('tooltips.sellerTaxRegNo')"
        :tax-reg-no-required="true"
    />

    <!-- Buyer Information -->
    <h3 class="mt-5 mb-2">{{ $t('form.buyerInformation') }}</h3>
    <div v-if="customerOptions.length" class="row g-3 mb-2">
      <div class="col-md-12">
        <SearchableDropdown
          input-id="buyerCustomerSelect"
          label="Kunde (Stammdaten)"
          :model-value="selectedCustomerKey"
          @update:modelValue="onCustomerSelected"
          :options="customerOptions"
        />
      </div>
    </div>
    <div class="row g-3">
      <div class="col-md-6">
        <div class="form-group">
          <label for="buyerName" class="form-label">
            <span data-bs-toggle="tooltip"
                  :data-bs-title="$t('tooltips.buyerName')"
                  class="badge ehw-green">BT-44</span>{{ $t('form.buyerName') }}
          </label>
          <input
              type="text"
              class="form-control"
              :class="{ 'is-invalid': uxStore.hasParsingError('BuyerName') }"
              id="buyerName"
              v-model="localInvoice.buyerName"
              @blur="emitUpdate('BuyerName')"
          />
          <div v-if="uxStore.hasParsingError('BuyerName')" class="invalid-feedback">
            {{ uxStore.getParsingErrorMessage('BuyerName') }}
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="form-group">
          <label for="buyerPostcode" class="form-label">
            <span data-bs-toggle="tooltip"
                  :data-bs-title="$t('tooltips.buyerPostcode')"
                  class="badge ehw-green">BT-53</span>{{ $t('form.buyerPostcode') }}
          </label>
          <input
              type="text"
              class="form-control"
              :class="{ 'is-invalid': uxStore.hasParsingError('BuyerPostcode') }"
              id="buyerPostcode"
              v-model="localInvoice.buyerPostcode"
              @blur="emitUpdate('BuyerPostcode')"
          />
          <div v-if="uxStore.hasParsingError('BuyerPostcode')" class="invalid-feedback">
            {{ uxStore.getParsingErrorMessage('BuyerPostcode') }}
          </div>
        </div>
      </div>
    </div>
    <div class="row g-3 mt-2">
      <div class="col-md-6">
        <div class="form-group">
          <label for="buyerCity" class="form-label">
            <span data-bs-toggle="tooltip"
                  :data-bs-title="$t('tooltips.buyerCity')"
                  class="badge ehw-green">BT-52</span>{{ $t('form.buyerCity') }}
          </label>
          <input
              type="text"
              class="form-control"
              :class="{ 'is-invalid': uxStore.hasParsingError('BuyerCity') }"
              id="buyerCity"
              v-model="localInvoice.buyerCity"
              @blur="emitUpdate('BuyerCity')"
          />
        </div>
        <div v-if="uxStore.hasParsingError('BuyerCity')" class="invalid-feedback">
          {{ uxStore.getParsingErrorMessage('BuyerCity') }}
        </div>
      </div>
      <div class="col-md-6">
        <div class="form-group">
          <label for="buyerStreet" class="form-label">
            <span data-bs-toggle="tooltip"
                  :data-bs-title="$t('tooltips.buyerStreet')"
                  class="badge ehw-green">BT-50</span>{{ $t('form.buyerStreet') }}
          </label>
          <input
              type="text"
              class="form-control"
              :class="{ 'is-invalid': uxStore.hasParsingError('BuyerStreet') }"
              id="buyerStreet"
              v-model="localInvoice.buyerStreet"
              @blur="emitUpdate('BuyerStreet')"
          />
          <div v-if="uxStore.hasParsingError('BuyerStreet')" class="invalid-feedback">
            {{ uxStore.getParsingErrorMessage('BuyerStreet') }}
          </div>
        </div>
      </div>
    </div>
    <div class="row g-3 mt-2">
      <div class="col-md-6">
        <div class="form-group">
          <SearchableDropdown
              input-id="buyerCountryCode"
              :label="$t('form.buyerCountryCode')"
              :model-value="localInvoice.buyerCountryCode"
              @update:modelValue="val => { localInvoice.buyerCountryCode = val; emitUpdate('BuyerCountryCode'); }"
              :options="masterDataStore.countryCodes"
              :has-parsing-error="uxStore.hasParsingError('BuyerCountryCode')"
              bt-number="BT-55"
              :bt-tooltip="$t('tooltips.buyerCountryCode')"
          />
          <div v-if="uxStore.hasParsingError('BuyerCountryCode')" class="invalid-feedback" style="padding-right: 27px;">
            <p>
              {{ uxStore.getParsingErrorMessage('BuyerCountryCode') }}
            </p>
            <button
                data-bs-toggle="tooltip"
                :data-bs-title="$t('tooltips.useDefaultValues')"
                class="btn py-0" type="button"
                @click.prevent="clearParsingErrorWithTooltip('BuyerCountryCode', $event)">
              <svg data-slot="icon" style="width: 27px; height: 27px; color: #dc3545" fill="none" stroke-width="1.5"
                   stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round"
                      d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="form-group">
          <label for="buyerGlobalID" class="form-label"><span data-bs-toggle="tooltip"
                                                              :data-bs-title="$t('tooltips.buyerGlobalID')"
                                                              class="badge ehw-green">BT-46</span>{{
              $t('form.buyerGlobalID')
            }}</label>
          <input
              type="text"
              class="form-control"
              :class="{ 'is-invalid': uxStore.hasParsingError('BuyerGlobalID') }"
              id="buyerGlobalID"
              v-model="localInvoice.buyerGlobalID"
              @blur="emitUpdate('BuyerGlobalID')"
          />
        </div>
      </div>
      <div class="col-md-3">
        <div class="form-group">
          <SearchableDropdown input-id="buyerGlobalIDSchemeIdentifier"
                              :label="$t('form.buyerGlobalIDSchemeIdentifier')"
                              :model-value="localInvoice.buyerGlobalIDSchemeIdentifier"
                              :has-parsing-error="uxStore.hasParsingError('BuyerGlobalIDSchemeIdentifier')"
                              :options="masterDataStore.globalIdSchemeIdentifiers"
                              @update:modelValue="val => { localInvoice.buyerGlobalIDSchemeIdentifier = val; emitUpdate('BuyerGlobalIDSchemeIdentifier'); }"

          />
          <div v-if="uxStore.hasParsingError('BuyerGlobalIDSchemeIdentifier')" class="invalid-feedback">
            {{ uxStore.getParsingErrorMessage('BuyerGlobalIDSchemeIdentifier') }}
          </div>
        </div>
      </div>
    </div>
    <div class="row g-3 mt-2">
      <div class="col-md-6">
        <div class="form-group">
          <label for="buyerElectronicAddress" class="form-label"><span data-bs-toggle="tooltip"
                                                                       :data-bs-title="$t('tooltips.buyerElectronicAddress')"
                                                                       class="badge ehw-green">BT-49</span>{{
              $t('form.buyerElectronicAddress')
            }}</label>
          <input
              type="text"
              class="form-control"
              :class="{ 'is-invalid': uxStore.hasParsingError('BuyerElectronicAddress') }"
              id="buyerElectronicAddress"
              v-model="localInvoice.buyerElectronicAddress"
              @blur="emitUpdate('BuyerElectronicAddress')"
          />
          <div v-if="uxStore.hasParsingError('BuyerElectronicAddress')" class="invalid-feedback">
            {{ uxStore.getParsingErrorMessage('BuyerElectronicAddress') }}
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="form-group">
          <label for="buyerReference" class="form-label"><span data-bs-toggle="tooltip"
                                                               :data-bs-title="$t('tooltips.buyerReference')"
                                                               class="badge ehw-green">BT-10</span>{{
              $t('form.buyerReference')
            }}</label>
          <input
              type="text"
              class="form-control"
              :class="{ 'is-invalid': uxStore.hasParsingError('BuyerReference') }"
              id="buyerReference"
              v-model="localInvoice.buyerReference"
              @blur="emitUpdate('BuyerReference')"
          />
          <div v-if="uxStore.hasParsingError('BuyerReference')" class="invalid-feedback">
            {{ uxStore.getParsingErrorMessage('BuyerReference') }}
          </div>
        </div>
      </div>
    </div>

    <!-- Buyer Contact Toggle and Form (unchanged) -->
    <h3 class="mt-5 mb-4 d-flex justify-content-start gap-3 align-items-center">
      <span>{{ $t('form.buyerContact') }}</span>
      <!-- Bootstrap Switch -->
      <span class="form-check form-switch mb-0 d-flex justify-content-start mb-2">
        <input
            class="form-check-input"
            type="checkbox"
            id="toggleBuyerContact"
            v-model="showBuyerContact"
            style="background-color: #ADC22D; border-color: #ADC22D;"
        />
      </span>
    </h3>

    <ContactForm
        v-if="showBuyerContact"
        :contact="localInvoice.buyerContact"
        label="Buyer Contact"
        @update-contact="updateBuyerContact"
    />


    <h3 class="mt-5 mb-4">{{ $t('form.buyerTaxRegistration') }}</h3>
    <TaxRegistrationForm
        :taxRegistration="localInvoice.buyerTaxRegistrations"
        @update-tax-registration="updateBuyerTaxRegistration"
        bt-number="BT-48"
        :bt-tooltip="$t('tooltips.buyerTaxRegNo')"
    />

    <!-- Bank and Payment Information -->
    <h3 class="mt-5 mb-4">{{ $t('form.bankAndPaymentInformation') }}</h3>
    <div class="row g-3">
      <div class="col-md-6">
        <div class="form-group">
          <label for="iban" class="form-label">
            <span data-bs-toggle="tooltip"
                  :data-bs-title="$t('tooltips.iban')"
                  class="badge ehw-green">BT-84</span>{{ $t('form.iban') }}
          </label>
          <input
              type="text"
              class="form-control"
              id="iban"
              v-model="localInvoice.iban"
              @blur="emitUpdate"
          />
        </div>
      </div>
      <div class="col-md-6">
        <div class="form-group">
          <label for="bic" class="form-label">
            <span data-bs-toggle="tooltip"
                  :data-bs-title="$t('tooltips.bic')"
                  class="badge ehw-green">BT-86</span>{{ $t('form.bic') }}
          </label>
          <input
              type="text"
              class="form-control"
              :class="{ 'is-invalid': uxStore.hasParsingError('Bic') }"
              id="bic"
              v-model="localInvoice.bic"
              @blur="emitUpdate('Bic')"
          />
          <div v-if="uxStore.hasParsingError('Bic')" class="invalid-feedback">
            {{ uxStore.getParsingErrorMessage('Bic') }}
          </div>
        </div>
      </div>
    </div>

    <div class="row g-3 mt-2">
      <div class="col-md-6">
        <div class="form-group">
          <label for="bankName" class="form-label">{{ $t('form.bankName') }}</label>
          <input
              type="text"
              class="form-control"
              :class="{ 'is-invalid': uxStore.hasParsingError('BankName') }"
              id="bankName"
              v-model="localInvoice.bankName"
              @blur="emitUpdate('BankName')"
          />
          <div v-if="uxStore.hasParsingError('BankName')" class="invalid-feedback">
            {{ uxStore.getParsingErrorMessage('BankName') }}
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="form-group">
          <div v-if="paymentTerms.length" class="mb-2">
            <label for="paymentTermSelect" class="form-label">Zahlungsbedingung (Stammdaten)</label>
            <select
              id="paymentTermSelect"
              v-model="selectedPaymentTermId"
              class="form-select"
              @change="onPaymentTermChanged"
            >
              <option value="">-</option>
              <option v-for="term in paymentTerms" :key="term.id" :value="term.id">
                {{ term.name }} ({{ term.daysUntilDue }} Tage)
              </option>
            </select>
            <div v-if="selectedPaymentTermId && !localInvoice.invoiceDate" class="text-muted small mt-1">
              Setze zuerst das Rechnungsdatum, um den Text zu generieren.
            </div>
            <div v-if="computedDueDateText" class="text-muted small mt-1">
              Fällig am: <strong>{{ computedDueDateText }}</strong>
            </div>
          </div>

          <label for="paymentTerms" class="form-label">
            <span data-bs-toggle="tooltip"
                  :data-bs-title="$t('tooltips.paymentTerms')"
                  class="badge ehw-green">BT-82</span>{{ $t('form.paymentTerms') }}
          </label>
          <textarea
              class="form-control"
              :class="{ 'is-invalid': uxStore.hasParsingError('PaymentTerms') }"
              id="paymentTerms"
              v-model="localInvoice.paymentTerms"
              @input="onPaymentTermsInput"
              @blur="emitUpdate('PaymentTerms')"
              required
          ></textarea>
          <div v-if="uxStore.hasParsingError('PaymentTerms')" class="invalid-feedback">
            {{ uxStore.getParsingErrorMessage('PaymentTerms') }}
          </div>
        </div>
      </div>
    </div>

    <!-- Trade Line Items -->
    <h3 class="mt-5 d-flex align-items-center gap-2 p-2 rounded-2"
        :class="{ 'is-invalid border border-1 mb-1': uxStore.hasParsingError('TradeLineItems'),
        'mb-4' : !uxStore.hasParsingError('TradeLineItems'),
    }"
    >
      <span data-bs-toggle="tooltip"
            :data-bs-title="$t('tooltips.tradeLineItems')"
            class="badge ehw-green"
      >BT-126</span>{{ $t('form.tradeLineItems') }}
    </h3>
    <div v-if="uxStore.hasParsingError('TradeLineItems')" class="invalid-feedback mb-3">
      {{ uxStore.getParsingErrorMessage('TradeLineItems') }}
    </div>
    <TradeLineItemsForm
        :standard-tax-rate="localInvoice.standardTaxRate"
        :items="localInvoice.tradeLineItems"
        :articles="articles"
        @update-items="updateTradeLineItems"
    />

    <!-- Notes -->
    <h3 class="mt-5 mb-4 p-2 rounded-2"
        :class="{ 'is-invalid border border-1': uxStore.hasParsingError('Notes') }"
    >{{ $t('form.notes') }}</h3>
    <div v-if="uxStore.hasParsingError('Notes')" class="invalid-feedback">
      {{ uxStore.getParsingErrorMessage('Notes') }}
    </div>
    <NotesForm
        :notes="localInvoice.notes"
        @update-notes="updateNotes"
    />
  </div>
</template>
<script>
import ContactForm from './ContactForm.vue';
import TaxRegistrationForm from './TaxRegistrationForm.vue';
import TradeLineItemsForm from './TradeLineItemsForm.vue';
import NotesForm from './NotesForm.vue';
import Invoice from '../../models/Invoice';
import {Tooltip} from "bootstrap";
import {useMasterDataStore} from "@/stores/masterDataStore";
import SearchableDropdown from "@/components/Shared/SearchableDropdown.vue";
import {useUxStore} from "@/stores/uxStore";
import CustomerService from "@/services/CustomerService";
import ArticleService from "@/services/ArticleService";
import CompanyService from "@/services/CompanyService";

export default {
  name: "InvoiceForm",
  components: {SearchableDropdown, ContactForm, TaxRegistrationForm, TradeLineItemsForm, NotesForm},
  props: {
    invoice: Object,
    isManualCreateMode: {
      type: Boolean,
      required: false,
      default: false,
    }
  },
  data() {
    return {
      localInvoice: this.invoice instanceof Invoice ? this.invoice : new Invoice(this.invoice || {}),
      masterDataStore: useMasterDataStore(),
      uxStore: useUxStore(),
      showBuyerContact: false, // Default state for buyer contact display
      showSellerContact: false, // Default state for seller contact display
      customers: [],
      customerOptions: [],
      selectedCustomerKey: "",
      articles: [],
      defaultCompany: null,
      paymentTerms: [],
      selectedPaymentTermId: "",
      paymentTermsAutoGenerated: false,
      lastGeneratedPaymentTerms: "",
    };
  },
  setup(props) {
    if (props.invoice.sellerCountryCode) {
      useMasterDataStore().addCountryCode(props.invoice.sellerCountryCode)
    }
    if (props.invoice.buyerCountryCode) {
      useMasterDataStore().addCountryCode(props.invoice.buyerCountryCode)
    }
    console.log("COUNRTYCODES", useMasterDataStore().selectedCountryCodes)
  },
  computed: {
    computedDueDateText() {
      const term = this.paymentTerms.find((t) => t.id === this.selectedPaymentTermId);
      if (!term || !this.localInvoice?.invoiceDate) {
        return "";
      }

      const due = this.computeDueDate(this.localInvoice.invoiceDate, term.daysUntilDue);
      return due ? this.formatDueDate(due) : "";
    },
    formattedInvoiceDate: {
      get() {
        return this.localInvoice.invoiceDate ? this.localInvoice.invoiceDate.slice(0, 10) : null;
      },
      set(value) {
        this.localInvoice.invoiceDate = value;
      }
    },
    formattedBillingPeriodStart: {
      get() {
        return this.localInvoice.billingPeriodStart ? this.localInvoice.billingPeriodStart.slice(0, 10) : null;
      },
      set(value) {
        this.localInvoice.billingPeriodStart = value;
      }
    },
    formattedBillingPeriodEnd: {
      get() {
        return this.localInvoice.billingPeriodEnd ? this.localInvoice.billingPeriodEnd.slice(0, 10) : null;
      },
      set(value) {
        this.localInvoice.billingPeriodEnd = value;
      }
    }
  },
  methods: {
    getCustomerKey(customer) {
      return (customer?.customerNumber || customer?.id || "").toString();
    },
    buildCustomerOptions(customers) {
      const list = Array.isArray(customers) ? customers : [];
      return list.map((c) => ({
        id: this.getCustomerKey(c),
        name: c?.name || this.getCustomerKey(c),
        shortName: c?.customerNumber || "",
      }));
    },
    async loadAdministrationData() {
      try {
        const [customersResponse, articlesResponse, companyResponse] = await Promise.all([
          CustomerService.getAll(null, 1, 200),
          ArticleService.getAll(null, 1, 200),
          CompanyService.getDefault(),
        ]);

        this.customers = Array.isArray(customersResponse?.data) ? customersResponse.data : [];
        this.customerOptions = this.buildCustomerOptions(this.customers);

        this.articles = Array.isArray(articlesResponse?.data) ? articlesResponse.data : [];

        this.defaultCompany = companyResponse?.data || null;
        this.paymentTerms = Array.isArray(this.defaultCompany?.paymentTerms) ? this.defaultCompany.paymentTerms : [];

        // For new, non-parsed invoices we prefill seller data and apply the default payment term.
        if (this.isManualCreateMode && this.defaultCompany) {
          this.tryPrefillSellerFromCompany(this.defaultCompany);
          this.trySelectDefaultPaymentTerm(this.defaultCompany);
        }
      } catch (error) {
        console.error("Failed to load administration data:", error);
      }
    },
    tryPrefillSellerFromCompany(company) {
      const hasSellerData =
        !!this.localInvoice?.sellerName ||
        !!this.localInvoice?.sellerStreet ||
        !!this.localInvoice?.sellerPostcode ||
        !!this.localInvoice?.sellerCity ||
        !!this.localInvoice?.sellerElectronicAddress ||
        (Array.isArray(this.localInvoice?.sellerTaxRegistrations) && this.localInvoice.sellerTaxRegistrations.length > 0);

      if (hasSellerData) {
        return;
      }

      this.localInvoice.sellerName = company.name || this.localInvoice.sellerName;
      this.localInvoice.sellerStreet = company.street || this.localInvoice.sellerStreet;
      this.localInvoice.sellerPostcode = company.postcode || this.localInvoice.sellerPostcode;
      this.localInvoice.sellerCity = company.city || this.localInvoice.sellerCity;
      this.localInvoice.sellerCountryCode = company.countryCode || this.localInvoice.sellerCountryCode;
      this.localInvoice.sellerGlobalID = company.globalID || this.localInvoice.sellerGlobalID;
      this.localInvoice.sellerGlobalIDSchemeIdentifier =
        company.globalIDSchemeIdentifier || this.localInvoice.sellerGlobalIDSchemeIdentifier;
      this.localInvoice.sellerElectronicAddress = company.electronicAddress || this.localInvoice.sellerElectronicAddress;

      if (!this.localInvoice.iban) this.localInvoice.iban = company.iban || this.localInvoice.iban;
      if (!this.localInvoice.bic) this.localInvoice.bic = company.bic || this.localInvoice.bic;
      if (!this.localInvoice.bankName) this.localInvoice.bankName = company.bankName || this.localInvoice.bankName;

      if (!this.localInvoice.sellerContact && (company.contactName || company.contactOrgUnit || company.contactEmailAddress || company.contactPhoneNo)) {
        this.localInvoice.sellerContact = {
          contactType: "Seller",
          name: company.contactName || "",
          orgUnit: company.contactOrgUnit || "",
          emailAddress: company.contactEmailAddress || "",
          phoneNo: company.contactPhoneNo || "",
        };
        this.showSellerContact = true;
      }

      if (!Array.isArray(this.localInvoice.sellerTaxRegistrations) || this.localInvoice.sellerTaxRegistrations.length === 0) {
        const regs = Array.isArray(company.taxRegistrations) ? company.taxRegistrations : [];
        this.localInvoice.sellerTaxRegistrations = regs
          .filter((r) => r?.taxRegNo)
          .map((r) => ({
            taxRegNo: r.taxRegNo,
            taxSchemeID: r.taxSchemeID || null,
          }));
      }

      this.emitUpdate();
    },
    trySelectDefaultPaymentTerm(company) {
      if (!company || !Array.isArray(company.paymentTerms) || company.paymentTerms.length === 0) {
        return;
      }

      if (this.selectedPaymentTermId) {
        return;
      }

      const defaultTerm = company.paymentTerms.find((t) => t.isDefault) || company.paymentTerms[0];
      if (!defaultTerm) {
        return;
      }

      // Only preselect + auto-generate if the invoice doesn't already have payment terms text.
      if (this.localInvoice.paymentTerms) {
        return;
      }

      this.selectedPaymentTermId = defaultTerm.id;
      this.paymentTermsAutoGenerated = true;
      this.generateAndApplyPaymentTermsIfPossible();
    },
    onCustomerSelected(key) {
      this.selectedCustomerKey = key || "";
      if (!this.selectedCustomerKey) {
        return;
      }

      const customer = this.customers.find((c) => this.getCustomerKey(c) === this.selectedCustomerKey);
      if (!customer) {
        return;
      }

      this.localInvoice.buyerName = customer.name || this.localInvoice.buyerName;
      this.localInvoice.buyerStreet = customer.street || this.localInvoice.buyerStreet;
      this.localInvoice.buyerPostcode = customer.postcode || this.localInvoice.buyerPostcode;
      this.localInvoice.buyerCity = customer.city || this.localInvoice.buyerCity;
      this.localInvoice.buyerCountryCode = customer.countryCode || this.localInvoice.buyerCountryCode;
      this.localInvoice.buyerGlobalID = customer.globalID || this.localInvoice.buyerGlobalID;
      this.localInvoice.buyerGlobalIDSchemeIdentifier =
        customer.globalIDSchemeIdentifier || this.localInvoice.buyerGlobalIDSchemeIdentifier;
      this.localInvoice.buyerElectronicAddress = customer.electronicAddress || this.localInvoice.buyerElectronicAddress;

      if (customer.contactName || customer.contactOrgUnit || customer.contactEmailAddress || customer.contactPhoneNo) {
        this.localInvoice.buyerContact = {
          contactType: "Buyer",
          name: customer.contactName || "",
          orgUnit: customer.contactOrgUnit || "",
          emailAddress: customer.contactEmailAddress || "",
          phoneNo: customer.contactPhoneNo || "",
        };
        this.showBuyerContact = true;
      }

      if (Array.isArray(customer.taxRegistrations)) {
        this.localInvoice.buyerTaxRegistrations = customer.taxRegistrations
          .filter((r) => r?.taxRegNo)
          .map((r) => ({ taxRegNo: r.taxRegNo, taxSchemeID: r.taxSchemeID || null }));
      }

      this.emitUpdate();
    },
    onPaymentTermChanged() {
      // User selection => auto-generate again unless they manually edited afterwards.
      this.paymentTermsAutoGenerated = true;
      this.generateAndApplyPaymentTermsIfPossible();
    },
    onPaymentTermsInput() {
      // Manual override disables further automatic recalculation.
      this.paymentTermsAutoGenerated = false;
    },
    computeDueDate(invoiceDateIso, daysUntilDue) {
      if (!invoiceDateIso) return null;
      const date = new Date(`${invoiceDateIso}T00:00:00`);
      if (Number.isNaN(date.getTime())) return null;
      const days = Number(daysUntilDue || 0);
      date.setDate(date.getDate() + days);
      return date;
    },
    formatDueDate(date) {
      return new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }).format(date);
    },
    generatePaymentTermsText(term) {
      if (!term) return "";
      const dueDate = this.computeDueDate(this.localInvoice.invoiceDate, term.daysUntilDue);
      if (!dueDate) return "";

      const template = term.textTemplate && term.textTemplate.trim().length > 0
        ? term.textTemplate
        : "Zahlbar bis {DUE_DATE} ohne Abzug. ({DAYS} Tage)";

      return template
        .replaceAll("{DAYS}", String(term.daysUntilDue))
        .replaceAll("{DUE_DATE}", this.formatDueDate(dueDate));
    },
    generateAndApplyPaymentTermsIfPossible() {
      if (!this.selectedPaymentTermId) {
        return;
      }

      const term = this.paymentTerms.find((t) => t.id === this.selectedPaymentTermId);
      if (!term) {
        return;
      }

      // Only auto-update if user didn't manually override, or if the current value is the one we generated last.
      if (!this.paymentTermsAutoGenerated && this.localInvoice.paymentTerms && this.localInvoice.paymentTerms !== this.lastGeneratedPaymentTerms) {
        return;
      }

      // Don't generate until invoice date exists.
      if (!this.localInvoice.invoiceDate) {
        return;
      }

      const text = this.generatePaymentTermsText(term);
      if (!text) {
        return;
      }

      this.localInvoice.paymentTerms = text;
      this.lastGeneratedPaymentTerms = text;
      this.emitUpdate();
    },
    emitUpdate(fieldName) {
      if (fieldName && this.localInvoice[fieldName.charAt(0).toLowerCase() + fieldName.slice(1)]) {
        this.uxStore.clearParsingError(fieldName);
      }
      this.$emit("update-invoice", this.localInvoice);
    },
    updateInvoiceDate(event) {
      this.localInvoice.invoiceDate = event.target.value;
      this.emitUpdate('InvoiceDate');
      this.generateAndApplyPaymentTermsIfPossible();
    },
    updateBillingPeriodStart(event) {
      this.localInvoice.billingPeriodStart = event.target.value;
      this.emitUpdate("BillingPeriodStart");
    },
    updateBillingPeriodEnd(event) {
      this.localInvoice.billingPeriodEnd = event.target.value;
      this.emitUpdate("BillingPeriodEnd");
    },
    updateSellerContact(contact) {
      this.localInvoice.sellerContact = contact;
      this.emitUpdate();
    },
    updateBuyerContact(contact) {
      this.localInvoice.buyerContact = contact;
      this.emitUpdate();
    },
    updateSellerTaxRegistration(taxRegistrations) {
      this.localInvoice.sellerTaxRegistrations = taxRegistrations;
      this.emitUpdate();
    },
    updateBuyerTaxRegistration(taxRegistrations) {
      this.localInvoice.buyerTaxRegistrations = taxRegistrations;
      this.emitUpdate();
    },
    updateNotes(notes) {
      if (notes) {
        this.uxStore.clearParsingError('Notes')
      }
      this.localInvoice.notes = [...notes];
      this.emitUpdate();
    },
    updateTradeLineItems(items) {
      if (items) {
        this.uxStore.clearParsingError('TradeLineItems')
      }
      this.localInvoice.tradeLineItems = [...items];
      this.emitUpdate();
    },
    preCheckContactVisibility() {
      // If editing and seller contact has data, show seller contact form
      if (this.localInvoice.sellerContact && (
          this.localInvoice.sellerContact.name ||
          this.localInvoice.sellerContact.orgUnit ||
          this.localInvoice.sellerContact.emailAddress ||
          this.localInvoice.sellerContact.phoneNo
      )) {
        this.showSellerContact = true;
      }

      // If editing and buyer contact has data, show buyer contact form
      if (this.localInvoice.buyerContact && (
          this.localInvoice.buyerContact.name ||
          this.localInvoice.buyerContact.orgUnit ||
          this.localInvoice.buyerContact.emailAddress ||
          this.localInvoice.buyerContact.phoneNo
      )) {
        this.showBuyerContact = true;
      }
    },
    initTooltips() {
      const tooltipTriggerList = this.$el.querySelectorAll('[data-bs-toggle="tooltip"]');
      tooltipTriggerList.forEach((tooltipTriggerEl) => {
        new Tooltip(tooltipTriggerEl);
      });
    },
    clearParsingErrorWithTooltip(fieldName, event) {
      const button = event.currentTarget;
      const tooltipInstance = Tooltip.getInstance(button);
      if (tooltipInstance) {
        tooltipInstance.dispose();
      }
      this.uxStore.clearParsingError(fieldName);
    }
  },
  watch: {
    invoice: {
      immediate: true,
      handler(newVal) {
        this.localInvoice = {...newVal};
        // After updating local invoice, re-check contact visibility
        this.$nextTick(() => this.preCheckContactVisibility());
        this.$nextTick(() => this.initTooltips());

      }
    },
    showBuyerContact: {
      handler(newVal) {
        if (!newVal) {
          this.localInvoice.buyerContact = null;
          this.emitUpdate();
        }
        this.$nextTick(() => this.initTooltips());
      }
    },
    showSellerContact: {
      handler(newVal) {
        if (!newVal) {
          this.localInvoice.sellerContact = null;
          this.emitUpdate();
        }
        this.$nextTick(() => this.initTooltips());
      }
    },
    "localInvoice.sellerCountryCode": {
      handler(newVal, oldVal) {
        this.masterDataStore.addCountryCode(newVal, oldVal)
      }
    },
    "localInvoice.buyerCountryCode": {
      handler(newVal, oldVal) {
        this.masterDataStore.addCountryCode(newVal, oldVal)
      }
    },
  },
  mounted() {
    this.initTooltips();
    this.preCheckContactVisibility();
    this.loadAdministrationData();
  }
};
</script>

<style scoped>
.form-label {
  display: flex;
  align-items: center;
  gap: 6px;
}

.form-switch .form-check-input {
  background-color: #ADC22D !important;
  border-color: #ADC22D !important;
}

.is-invalid {
  border-color: #dc3545 !important;
}

.invalid-feedback {
  display: flex;
  justify-content: space-between;
  align-items: start;
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.875em;
  color: #dc3545;
}
</style>
